import _axios from "axios";

const ADMIN_TOKEN_KEY = "admin_token";
const ADMIN_PROFILE_KEY = "admin_profile";

export type AdminProfile = {
  id: string;
  email: string;
  role: string;
};

export type AdminLoginResponse = {
  token: string;
  admin: AdminProfile;
};

function getStoredToken(): string | null {
  if (typeof window === "undefined") return null;
  return localStorage.getItem(ADMIN_TOKEN_KEY);
}

function getStoredProfile(): AdminProfile | null {
  if (typeof window === "undefined") return null;
  const raw = localStorage.getItem(ADMIN_PROFILE_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as AdminProfile;
  } catch {
    return null;
  }
}

export function getAdminToken(): string | null {
  return getStoredToken();
}

export function getAdminProfile(): AdminProfile | null {
  return getStoredProfile();
}

export function isAdminLoggedIn(): boolean {
  return !!getStoredToken();
}

export function setAdminAuth(token: string, admin: AdminProfile): void {
  localStorage.setItem(ADMIN_TOKEN_KEY, token);
  localStorage.setItem(ADMIN_PROFILE_KEY, JSON.stringify(admin));
}

export function clearAdminAuth(): void {
  localStorage.removeItem(ADMIN_TOKEN_KEY);
  localStorage.removeItem(ADMIN_PROFILE_KEY);
}

export function adminLogout(): void {
  clearAdminAuth();
  if (typeof window !== "undefined") window.location.href = "/admin/login";
}

const adminAxios = _axios.create({ baseURL: "/api" });

adminAxios.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

adminAxios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      clearAdminAuth();
      if (typeof window !== "undefined") window.location.href = "/admin/login";
    }
    return Promise.reject(error instanceof Error ? error : new Error("Request failed"));
  },
);

export const adminApi = {
  login: async (email: string, password: string): Promise<AdminLoginResponse> => {
    const { data } = await _axios.post<AdminLoginResponse>("/api/admin/login", {
      email,
      password,
    });
    return data;
  },

  getDashboardStats: async () => {
    const { data } = await adminAxios.get("/admin/dashboard/stats");
    return data;
  },

  getPayments: async (params?: {
    status?: string;
    dateFrom?: string;
    dateTo?: string;
    limit?: number;
    offset?: number;
  }) => {
    const { data } = await adminAxios.get("/admin/payments", { params });
    return data;
  },

  getUsers: async (params?: {
    subscriptionStatus?: string;
    search?: string;
    limit?: number;
    offset?: number;
  }) => {
    const { data } = await adminAxios.get("/admin/users", { params });
    return data;
  },

  getSubscriptions: async (params?: { status?: string; expiringSoon?: boolean }) => {
    const { data } = await adminAxios.get("/admin/subscriptions", { params });
    return data;
  },

  manageSubscription: async (
    id: string,
    action: "extend" | "cancel" | "reactivate",
    days?: number,
  ) => {
    const { data } = await adminAxios.patch(`/admin/subscriptions/${id}`, {
      action,
      days,
    });
    return data;
  },

  getPlans: async () => {
    const { data } = await adminAxios.get<AdminPlan[]>("/admin/plans");
    return data;
  },

  seedPlans: async () => {
    const { data } = await adminAxios.post<AdminPlan[]>("/admin/plans/seed");
    return data;
  },

  updatePlan: async (
    id: string,
    body: { price?: number; durationDays?: number | null; isActive?: boolean },
  ) => {
    const { data } = await adminAxios.patch<AdminPlan>(`/admin/plans/${id}`, body);
    return data;
  },
};

export type AdminPlan = {
  id: string;
  name: string;
  price: number;
  durationDays: number | null;
  downloadsAllowed: number;
  isActive: boolean;
  createdAt: string;
};
